import { Link } from "react-router-dom";
import { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { CONTACT, BSPORT } from "@/data/config";
import { track } from "@/context/LanguageContext";
import { Logo } from "@/components/Logo";
import { API } from "@/lib/api";

function NewsletterSignup() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    if (!email || status === "sending") return;
    setStatus("sending");
    try {
      const res = await fetch(`${API}/newsletter-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error("failed");
      setStatus("success");
      setEmail("");
      track("newsletter_signup", { source: "footer" });
    } catch {
      setStatus("error");
    }
  };
  return (
    <div className="mt-10 max-w-sm" data-testid="footer-newsletter">
      <p className="label text-blush">{t.footer.newsLabel}</p>
      <p className="mt-3 font-sans text-sm leading-relaxed text-petal/70">{t.footer.newsBody}</p>
      {status === "success" ? (
        <p className="mt-4 font-sans text-sm text-blush" data-testid="footer-newsletter-success">{t.footer.newsSuccess}</p>
      ) : (
        <form onSubmit={submit} className="mt-4 flex border border-petal/30">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.footer.newsPlaceholder}
            className="w-full bg-transparent px-4 py-3 font-sans text-sm text-petal placeholder:text-petal/40 focus:outline-none"
            data-testid="footer-newsletter-email"
          />
          <button
            type="submit"
            disabled={status === "sending"}
            className="label shrink-0 bg-petal px-5 py-3 text-onyx transition-colors duration-500 hover:bg-blush disabled:opacity-60"
            data-testid="footer-newsletter-submit"
          >
            {status === "sending" ? "…" : t.footer.newsCta}
          </button>
        </form>
      )}
      {status === "error" && <p className="mt-3 font-sans text-xs text-terracotta" data-testid="footer-newsletter-error">{t.footer.newsError}</p>}
    </div>
  );
}

export function Footer() {
  const { t, lang } = useLang();
  const explore = [
    { to: "/classes", label: t.nav.classes },
    { to: "/pricing", label: t.nav.pricing },
    { to: "/schedule", label: t.nav.schedule },
    { to: "/events", label: t.nav.events },
    { to: "/about", label: t.nav.about },
    { to: "/faq", label: t.nav.faq },
    { to: "/corporate", label: t.nav.corporate },
    { to: "/gift-cards", label: t.nav.giftcards },
    { to: "/contact", label: t.nav.contact },
  ];
  return (
    <footer className="bg-onyx text-petal" data-testid="footer">
      <div className="mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-28">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Logo color="#FBF8F5" className="h-14 w-[280px] md:h-16 md:w-[320px]" />
            <p className="mt-6 max-w-sm font-sans text-sm leading-relaxed text-blush">{t.footer.tagline}</p>
            <p className="label mt-8 text-terracotta">{t.footer.city}</p>
            <a
              href={BSPORT.INTRO_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("intro_offer_click", { source: "footer" })}
              className="label mt-8 inline-block border border-petal px-8 py-4 transition-colors duration-500 hover:bg-petal hover:text-onyx"
              data-testid="footer-intro-cta"
            >
              {t.common.startIntro} · €59
            </a>
            <NewsletterSignup />
          </div>

          <div className="lg:col-span-3 lg:col-start-7">
            <p className="label text-blush">{t.footer.explore}</p>
            <ul className="mt-6 space-y-3">
              {explore.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="font-sans text-sm text-petal/80 transition-colors duration-300 hover:text-petal" data-testid={`footer-${l.to.slice(1)}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="label text-blush">{t.footer.connect}</p>
            <ul className="mt-6 space-y-3 font-sans text-sm text-petal/80">
              <li>{CONTACT.city}</li>
              <li className="text-petal/50">{CONTACT.address}</li>
              <li><a href={`mailto:${CONTACT.email}`} className="transition-colors hover:text-petal">{CONTACT.email}</a></li>
              <li><a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-petal">{t.footer.follow} {CONTACT.instagramHandle}</a></li>
            </ul>
            <p className="label mt-8 text-blush">{t.contact.hours}</p>
            <ul className="mt-4 space-y-1.5 font-sans text-sm text-petal/70" data-testid="footer-hours">
              {CONTACT.hours.map((row) => (
                <li key={row.h} className="flex justify-between gap-6">
                  <span>{row[lang]}</span>
                  <span className="text-petal/50">{row.h}</span>
                </li>
              ))}
            </ul>
            <p className="label mt-8 text-blush">{t.footer.legal}</p>
            <ul className="mt-4 space-y-2 font-sans text-sm text-petal/60">
              <li><Link to="/privacy" className="hover:text-petal">{t.footer.privacy}</Link></li>
              <li><Link to="/terms" className="hover:text-petal">{t.footer.terms}</Link></li>
              <li><Link to="/cookies" className="hover:text-petal">{t.footer.cookies}</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-20 flex flex-col gap-4 border-t border-petal/15 pt-8 md:flex-row md:items-center md:justify-between">
          <p className="label text-terracotta">Contrôle · Ligne · Force</p>
          <p className="font-sans text-xs text-petal/50">© {new Date().getFullYear()} Maison Core. {t.footer.rights}</p>
        </div>
      </div>
    </footer>
  );
}
